import React, { useState } from 'react';

/**
 * CryptoIcon renders real cryptocurrency logos from the cryptocurrency-icons CDN.
 * Falls back to a gradient circle with the symbol's first letter if the image fails.
 *
 * Usage:
 *   <CryptoIcon symbol="BTC" size={40} />
 *   <CryptoIcon symbol="ETH" size={32} className="shadow-lg" />
 */

const ICON_BASE = 'https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/128/color';

// Mapping symbols to their filename on the CDN (lowercase)
const SYMBOL_MAP = {
  BTC: 'btc',
  ETH: 'eth',
  SOL: 'sol',
  BNB: 'bnb',
  XRP: 'xrp',
  ADA: 'ada',
  USDT: 'usdt',
  USDC: 'usdc',
  DOGE: 'doge',
  DOT: 'dot',
  AVAX: 'avax',
  MATIC: 'matic',
  LINK: 'link',
  UNI: 'uni',
  ATOM: 'atom',
  LTC: 'ltc',
  SHIB: 'shib',
  TRX: 'trx',
  NEAR: 'near',
  FTM: 'ftm',
};

// Gradient colors per symbol for fallback & background ring
const GRADIENTS = {
  BTC: 'from-orange-500 to-amber-500',
  ETH: 'from-blue-500 to-indigo-500',
  SOL: 'from-purple-500 to-pink-500',
  BNB: 'from-yellow-500 to-orange-500',
  XRP: 'from-gray-500 to-slate-600',
  ADA: 'from-blue-600 to-cyan-500',
  USDT: 'from-green-500 to-emerald-500',
  USDC: 'from-blue-500 to-blue-600',
  DOGE: 'from-yellow-400 to-amber-500',
  DOT: 'from-pink-500 to-purple-600',
  AVAX: 'from-red-500 to-red-600',
  MATIC: 'from-purple-500 to-indigo-600',
  LINK: 'from-blue-600 to-blue-700',
  UNI: 'from-pink-500 to-pink-600',
  ATOM: 'from-indigo-500 to-purple-500',
};

const CryptoIcon = ({ symbol, size = 40, className = '' }) => {
  const [imgError, setImgError] = useState(false);
  const sym = symbol?.toUpperCase() || '';
  const fileName = SYMBOL_MAP[sym];
  const gradient = GRADIENTS[sym] || 'from-gray-500 to-gray-600';

  if (!fileName || imgError) {
    // Fallback: gradient circle with letter
    return (
      <div
        className={`bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        {sym.charAt(0) || '?'}
      </div>
    );
  }

  return (
    <img
      src={`${ICON_BASE}/${fileName}.png`}
      alt={sym}
      width={size}
      height={size}
      className={`rounded-xl flex-shrink-0 ${className}`}
      onError={() => setImgError(true)}
      loading="lazy"
    />
  );
};

export default CryptoIcon;
export { GRADIENTS };
